import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './PlayerPickerModal.css';

const PlayerPickerModal = ({ open, players, onClose, onSelect }) => {
    const [search, setSearch] = useState('');
    const inputRef = useRef(null);
    const overlayRef = useRef(null);

    // Reset search and autofocus when opened
    useEffect(() => {
        if (open) {
            setSearch('');
            // Delay focus slightly so the sheet animation starts first
            const timer = setTimeout(() => inputRef.current?.focus(), 100);
            return () => clearTimeout(timer);
        }
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open, onClose]);

    // Prevent body scroll while modal is open
    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    // Resize overlay to match visual viewport (handles virtual keyboard on iOS)
    useEffect(() => {
        if (!open || !window.visualViewport) return;
        const vv = window.visualViewport;
        const update = () => {
            if (overlayRef.current) {
                overlayRef.current.style.height = `${vv.height}px`;
                overlayRef.current.style.top = `${vv.offsetTop}px`;
            }
        };
        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        return () => {
            vv.removeEventListener('resize', update);
            vv.removeEventListener('scroll', update);
        };
    }, [open]);

    if (!open) return null;

    const filtered = players.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase())
    );

    return ReactDOM.createPortal(
        <div className="picker-modal-overlay" ref={overlayRef} onClick={onClose}>
            <div className="picker-modal-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="picker-modal-handle" />
                <div className="picker-modal-header">
                    <h3 className="picker-modal-title">Select Player</h3>
                    <button className="picker-modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="picker-modal-search-wrap">
                    <input
                        ref={inputRef}
                        type="text"
                        className="picker-modal-search"
                        placeholder="Search players..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="picker-modal-list">
                    {filtered.length === 0 ? (
                        <div className="picker-modal-empty">No players found</div>
                    ) : (
                        filtered.map(player => (
                            <div
                                key={player.id}
                                className="picker-modal-row"
                                onClick={() => onSelect(player)}
                            >
                                <span className="picker-modal-row-name">{player.name}</span>
                                <span className="picker-modal-row-hcp">{player.handicap}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PlayerPickerModal;
